import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const emptyItem = () => ({
  date: "", meal: "dejeuner", provider: "", menu: "",
  allergies: [], expected_count: 0, confirmed_count: 0, unit_price_eur: 0, notes: "",
});

export default function RestaurationTab({ locale = "fr" }) {
  const isFr = locale === "fr";
  const [data, setData] = useState({ items: [], total_expected: 0, total_confirmed: 0,
                                       estimated_cost_eur: 0, meal_labels: {}, meals: [] });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/admin/catering/items");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = {
      ...editing,
      expected_count: Number(editing.expected_count) || 0,
      confirmed_count: Number(editing.confirmed_count) || 0,
      unit_price_eur: Number(editing.unit_price_eur) || 0,
      allergies: typeof editing.allergies === "string"
        ? editing.allergies.split(",").map((s) => s.trim()).filter(Boolean)
        : (editing.allergies || []),
    };
    if (editing.id) await api.patch(`/admin/catering/items/${editing.id}`, payload);
    else await api.post("/admin/catering/items", payload);
    setEditing(null);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm(isFr ? "Supprimer ce repas ?" : "Delete this meal?")) return;
    await api.delete(`/admin/catering/items/${id}`);
    load();
  };

  // Group by date
  const byDate = {};
  for (const it of data.items) {
    const d = it.date || "—";
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(it);
  }

  return (
    <div className="space-y-6" data-testid="restauration-tab">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl">{isFr ? "Restauration" : "Catering"}</h2>
        <a className="ml-auto btn btn-outline btn-sm"
           data-testid="catering-export-csv"
           href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/catering/export.csv`}
           target="_blank" rel="noreferrer">
          📊 {isFr ? "Export CSV" : "Export CSV"}
        </a>
        <button className="btn btn-primary" onClick={() => setEditing(emptyItem())} data-testid="catering-add-btn">
          {isFr ? "+ Nouveau repas" : "+ New meal"}
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div className="admin-card text-center">
          <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
            {isFr ? "Attendus" : "Expected"}
          </div>
          <div className="font-display text-3xl" style={{ color: "var(--gold)" }} data-testid="catering-kpi-expected">
            {data.total_expected}
          </div>
        </div>
        <div className="admin-card text-center">
          <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
            {isFr ? "Confirmés" : "Confirmed"}
          </div>
          <div className="font-display text-3xl" style={{ color: "var(--cinnabar)" }} data-testid="catering-kpi-confirmed">
            {data.total_confirmed}
          </div>
        </div>
        <div className="admin-card text-center">
          <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
            {isFr ? "Coût estimé" : "Estimated cost"}
          </div>
          <div className="font-display text-3xl" data-testid="catering-kpi-cost">
            {data.estimated_cost_eur?.toFixed(2)} €
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {Object.keys(byDate).length === 0 && (
          <p className="font-body" style={{ color: "var(--muted)" }}>
            {isFr ? "Aucun repas planifié." : "No meals planned."}
          </p>
        )}
        {Object.keys(byDate).sort().map((d) => (
          <div key={d} className="space-y-2">
            <h3 className="font-caps text-[0.7rem]" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>
              {d === "—" ? (isFr ? "Sans date" : "No date") : d}
            </h3>
            {byDate[d].map((it) => (
              <div key={it.id} className="admin-card" data-testid={`catering-item-${it.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-caps text-[0.65rem] px-2 py-0.5 rounded-sm"
                            style={{ backgroundColor: "var(--gold)", color: "white", letterSpacing: "0.18em" }}>
                        {data.meal_labels[it.meal] || it.meal}
                      </span>
                      {it.provider && <span className="text-sm" style={{ color: "var(--ink-2)" }}>· {it.provider}</span>}
                    </div>
                    {it.menu && <p className="text-sm mt-1">{it.menu}</p>}
                    {it.allergies?.length > 0 && (
                      <p className="text-xs mt-1" style={{ color: "var(--cinnabar)" }}>
                        ⚠ {it.allergies.join(", ")}
                      </p>
                    )}
                    <div className="flex gap-4 text-xs mt-2" style={{ color: "var(--muted)" }}>
                      <span>{isFr ? "Attendus" : "Expected"} : <strong style={{ color: "var(--ink)" }}>{it.expected_count}</strong></span>
                      <span>{isFr ? "Confirmés" : "Confirmed"} : <strong style={{ color: "var(--ink)" }}>{it.confirmed_count}</strong></span>
                      {it.unit_price_eur > 0 && <span>{it.unit_price_eur} €/pers</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn btn-outline btn-sm" onClick={() => setEditing(it)} data-testid={`catering-edit-${it.id}`}>✎</button>
                    <button className="btn btn-outline btn-sm" onClick={() => remove(it.id)} data-testid={`catering-delete-${it.id}`}>✕</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {editing && (
        <CateringEditor item={editing} setItem={setEditing} onSave={save} onClose={() => setEditing(null)}
                        meals={data.meals} mealLabels={data.meal_labels} isFr={isFr} />
      )}
    </div>
  );
}

function CateringEditor({ item, setItem, onSave, onClose, meals, mealLabels, isFr }) {
  const update = (k, v) => setItem({ ...item, [k]: v });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(20,16,12,0.7)" }}>
      <div className="admin-card max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-4" data-testid="catering-editor">
        <h3 className="font-display text-xl">{item.id ? (isFr ? "Modifier" : "Edit") : (isFr ? "Nouveau repas" : "New meal")}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Date" : "Date"}</span>
            <input type="date" className="input-classic w-full" value={item.date} onChange={(e) => update("date", e.target.value)} data-testid="catering-input-date" />
          </label>
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Repas" : "Meal"}</span>
            <select className="input-classic w-full" value={item.meal} onChange={(e) => update("meal", e.target.value)} data-testid="catering-input-meal">
              {meals.map((m) => <option key={m} value={m}>{mealLabels[m] || m}</option>)}
            </select>
          </label>
        </div>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Prestataire" : "Provider"}</span>
          <input className="input-classic w-full" value={item.provider} onChange={(e) => update("provider", e.target.value)} data-testid="catering-input-provider" />
        </label>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>Menu</span>
          <textarea rows={3} className="input-classic w-full" value={item.menu} onChange={(e) => update("menu", e.target.value)} data-testid="catering-input-menu" />
        </label>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Allergies (séparées par des virgules)" : "Allergies (comma-separated)"}</span>
          <input className="input-classic w-full"
                 value={Array.isArray(item.allergies) ? item.allergies.join(", ") : item.allergies}
                 onChange={(e) => update("allergies", e.target.value)}
                 data-testid="catering-input-allergies" />
        </label>
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Attendus" : "Expected"}</span>
            <input type="number" className="input-classic w-full" value={item.expected_count} onChange={(e) => update("expected_count", e.target.value)} data-testid="catering-input-expected" />
          </label>
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Confirmés" : "Confirmed"}</span>
            <input type="number" className="input-classic w-full" value={item.confirmed_count} onChange={(e) => update("confirmed_count", e.target.value)} data-testid="catering-input-confirmed" />
          </label>
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Prix unit. €" : "Unit price €"}</span>
            <input type="number" step="0.01" className="input-classic w-full" value={item.unit_price_eur} onChange={(e) => update("unit_price_eur", e.target.value)} data-testid="catering-input-price" />
          </label>
        </div>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>Notes</span>
          <textarea rows={2} className="input-classic w-full" value={item.notes} onChange={(e) => update("notes", e.target.value)} data-testid="catering-input-notes" />
        </label>
        <div className="flex gap-2 pt-2">
          <button className="btn btn-primary" onClick={onSave} data-testid="catering-save-btn">{isFr ? "Enregistrer" : "Save"}</button>
          <button className="btn btn-outline btn-sm" onClick={onClose} data-testid="catering-cancel-btn">{isFr ? "Annuler" : "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}
